import type { JobItemDto } from "@artha/api-contracts";
import { normalizeCountryCode } from "./normalizers";

export const RESUME_ELIGIBILITY_CPA_THRESHOLDS = {
  IN: 4,
  US: 50,
} as const;

export type ResumeEligibilityCountry =
  keyof typeof RESUME_ELIGIBILITY_CPA_THRESHOLDS;

/**
 * How the resume-personalization sheet gates the apply:
 * - "none": no sheet, direct redirect.
 * - "fit": full gate — resume, review, job-fit check, OTP (high-value CPA).
 * - "collect_only": take and parse the resume, then redirect immediately —
 *   no job-fit check, no review, no OTP (remote RPH referral jobs).
 */
export type ResumeGateMode = "none" | "fit" | "collect_only";

export type ResumeEligibilityResult = {
  eligible: boolean;
  gate: ResumeGateMode;
  country: ResumeEligibilityCountry | null;
  cpaValue: number | null;
  threshold: number | null;
};

const isRemoteRphJob = (
  job: Partial<Pick<JobItemDto, "rph_value" | "work_mode">>,
): boolean =>
  typeof job.rph_value === "number" &&
  Number.isFinite(job.rph_value) &&
  job.rph_value > 0 &&
  String(job.work_mode ?? "").toLowerCase() === "remote";

export const evaluateResumeEligibility = (
  job: Pick<JobItemDto, "country" | "cpa_value"> &
    Partial<Pick<JobItemDto, "rph_value" | "work_mode">>,
): ResumeEligibilityResult => {
  const normalizedCountry = normalizeCountryCode(job.country || "");
  const country = Object.prototype.hasOwnProperty.call(
    RESUME_ELIGIBILITY_CPA_THRESHOLDS,
    normalizedCountry,
  )
    ? (normalizedCountry as ResumeEligibilityCountry)
    : null;
  const cpaValue =
    typeof job.cpa_value === "number" && Number.isFinite(job.cpa_value)
      ? job.cpa_value
      : null;
  const threshold = country ? RESUME_ELIGIBILITY_CPA_THRESHOLDS[country] : null;

  // Product decision (2026-09-01, revised): remote RPH jobs collect and parse
  // the candidate's resume but never run the job-fit gate — after the parse
  // succeeds they are redirected to the provider's referral flow.
  if (isRemoteRphJob(job)) {
    return {
      eligible: true,
      gate: "collect_only",
      country,
      cpaValue,
      threshold,
    };
  }

  const fitGated =
    country !== null &&
    cpaValue !== null &&
    threshold !== null &&
    cpaValue > threshold;

  return {
    eligible: fitGated,
    gate: fitGated ? "fit" : "none",
    country,
    cpaValue,
    threshold,
  };
};
